import { useContext } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import { FileFieldDeleteContext } from './FileField';

/**
 * A delete button for a file item inside a FileField itemTemplate.
 *
 * Reads ~.data.fid and ~.delete_url from the nearest TemplateContext (set by
 * FileField per-item) and calls the FileFieldDeleteContext callback on click.
 *
 * Only renders when the item has a fid (i.e. successfully uploaded, not pending).
 *
 * YAML usage (inside itemTemplate):
 *   - type: FileFieldDelete
 *     attributes:
 *       class: "text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
 *       title: "Supprimer le fichier"
 */
export const FileFieldDelete = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const removeFile = useContext(FileFieldDeleteContext);

    const attributes = useEvaluatedAttributes(props.attributes);

    const fid = evaluateTemplateValue({
        valueToEvaluate: '~.data.fid',
        globalDataContext,
        templateContext,
    });

    const deleteUrl = evaluateTemplateValue({
        valueToEvaluate: '~.delete_url',
        globalDataContext,
        templateContext,
    });

    // Only render for saved files (fid is set).
    if (!fid || !removeFile) return null;

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeFile(fid, deleteUrl);
    };

    return (
        <ActionDependant {...props}>
            <button
                type="button"
                onClick={handleClick}
                {...attributes}
            >
                {props.content ?? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                )}
            </button>
        </ActionDependant>
    );
};
